import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";

export default function Analytics() {
    const [summary, setSummary] = useState(null);
    const [courses, setCourses] = useState([]);
    const [assignments, setAssignments] = useState([]);

    useEffect(() => {
        (async () => {
            const [s, c, a] = await Promise.all([api.get("/analytics/summary"), api.get("/courses"), api.get("/assignments")]);
            setSummary(s.data); setCourses(c.data); setAssignments(a.data);
        })();
    }, []);

    const workloadData = courses.map(c => ({
        name: c.code || c.name.slice(0, 8),
        value: assignments.filter(a => a.course_id === c.id && a.status !== "done").length,
        color: c.color,
    })).filter(x => x.value > 0);

    return (
        <div className="space-y-8" data-testid="analytics-page">
            <header>
                <div className="mono-label">Analytics</div>
                <h1 className="font-display text-4xl font-medium tracking-tight mt-2">Your rhythm</h1>
                <p className="text-muted-foreground mt-1">Understand how you actually study.</p>
            </header>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                <Metric label="Total focus" value={`${Math.round((summary?.total_focus_minutes || 0) / 60 * 10) / 10}h`} />
                <Metric label="Sessions" value={summary?.total_sessions || 0} />
                <Metric label="Streak" value={`${summary?.streak_days || 0}d`} accent />
                <Metric label="Completion" value={`${summary?.completion_rate || 0}%`} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <section className="bg-card rounded-2xl border border-border/60 p-6">
                    <div className="mono-label mb-1">Last 7 days</div>
                    <h3 className="font-display text-xl font-medium mb-6">Focus trend</h3>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={summary?.last_7_days || []}>
                                <defs>
                                    <linearGradient id="fg" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="date" tickFormatter={d => new Date(d).toLocaleDateString(undefined, { weekday: "short" })} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                                <Area type="monotone" dataKey="minutes" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#fg)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </section>

                <section className="bg-card rounded-2xl border border-border/60 p-6">
                    <div className="mono-label mb-1">By course</div>
                    <h3 className="font-display text-xl font-medium mb-6">Workload distribution</h3>
                    {workloadData.length === 0 ? (
                        <div className="h-56 flex items-center justify-center text-sm text-muted-foreground">No open assignments to analyze.</div>
                    ) : (
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={workloadData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>
                                        {workloadData.map((d) => <Cell key={d.name} fill={d.color} />)}
                                    </Pie>
                                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                        {workloadData.map(w => (
                            <div key={w.name} className="flex items-center gap-1.5 text-xs">
                                <span className="w-2 h-2 rounded-full" style={{ background: w.color }} />{w.name} · {w.value}
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <section className="bg-card rounded-2xl border border-border/60 p-6">
                <div className="mono-label mb-1">Heatmap</div>
                <h3 className="font-display text-xl font-medium mb-6">Focus intensity</h3>
                <div className="grid grid-cols-7 gap-2 max-w-md">
                    {(summary?.last_7_days || []).map(d => {
                        const intensity = Math.min(1, (d.minutes || 0) / 90);
                        return (
                            <div key={d.date} className="aspect-square rounded-md flex flex-col items-center justify-center" style={{ background: `hsl(var(--primary) / ${0.1 + intensity * 0.75})` }}>
                                <div className="text-[10px] text-primary-foreground/70 font-mono">{new Date(d.date).getDate()}</div>
                                <div className="text-[10px] text-primary-foreground/80 font-mono">{d.minutes}m</div>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}

function Metric({ label, value, accent }) {
    return (
        <div className={`rounded-2xl border border-border/60 p-5 ${accent ? "bg-accent/10" : "bg-card"}`}>
            <div className="mono-label mb-3">{label}</div>
            <div className="font-display text-3xl font-medium tracking-tight">{value}</div>
        </div>
    );
}
